// Lead detail header — identity, sales stage controls, contact strip, and Google Places facts grid.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { safeHref } from '../../lib/safe-url.js';
import {
  IconCheck,
  IconCopy,
  IconExternalLink,
  IconFileText,
  IconGlobe,
  IconMap,
  IconPhone,
  IconStar,
} from '../../components/Icons.js';
import { useSetStage, useToggleDnc } from './useActivity.js';
import { useStages } from '../sales/useSales.js';
import { useDispatchRun } from '../pipeline/usePipeline.js';
import type { LeadDetailRow, PlacesCacheRow } from './useLeads.js';

const REANALYSE_STAGES = ['details', 'audit', 'identity', 'resolve', 'score', 'documents'] as const;

function weekdayLines(hours: unknown): string[] {
  if (typeof hours !== 'object' || hours === null) return [];
  const raw = (hours as { weekdayDescriptions?: unknown }).weekdayDescriptions;
  return Array.isArray(raw) ? raw.filter((x): x is string => typeof x === 'string') : [];
}

function ContactStrip({ lead }: { lead: LeadDetailRow }) {
  const [copied, setCopied] = useState(false);
  const copyPhone = () => {
    void navigator.clipboard.writeText(lead.phone ?? '');
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div className="lead-detail__contactstrip">
      <div className="lead-detail__phones">
        {lead.phone === null ? (
          <span className="badge badge--neutral">няма телефон</span>
        ) : (
          <>
            <a className="btn btn--primary" href={`tel:${lead.phone}`} title="Директно избиране">
              <IconPhone size={15} />
              <span>{lead.phone}</span>
            </a>
            <button
              type="button"
              className="btn btn--secondary btn--icon"
              onClick={copyPhone}
              title="Копирай телефонния номер"
            >
              {copied ? <IconCheck size={15} /> : <IconCopy size={15} />}
            </button>
            <a
              className="btn btn--secondary btn--sm"
              href={`viber://chat?number=${encodeURIComponent(lead.phone)}`}
              title="Отвори чат във Viber"
            >
              Viber
            </a>
            <a
              className="btn btn--secondary btn--sm"
              href={`https://wa.me/${lead.phone.replace(/\D/g, '')}`}
              target="_blank"
              rel="noreferrer noopener"
              title="Отвори в WhatsApp"
            >
              WhatsApp
            </a>
          </>
        )}
      </div>
      <div className="lead-detail__links">
        <Link className="btn btn--primary btn--sm" to={`/leads/${lead.id}/cue`}>
          <IconFileText size={14} />
          <span>Картонче за разговор</span>
        </Link>
        {lead.website_url && (
          <a
            className="btn btn--secondary btn--sm"
            href={safeHref(lead.website_url)}
            target="_blank"
            rel="noreferrer noopener"
          >
            <IconGlobe size={14} />
            <span>Сайт</span>
            <IconExternalLink size={12} />
          </a>
        )}
        {lead.maps_uri && (
          <a
            className="btn btn--secondary btn--sm"
            href={safeHref(lead.maps_uri)}
            target="_blank"
            rel="noreferrer noopener"
          >
            <IconMap size={14} />
            <span>Google Maps</span>
            <IconExternalLink size={12} />
          </a>
        )}
      </div>
    </div>
  );
}

function FactsGrid({
  lead,
  cached,
}: {
  lead: LeadDetailRow;
  cached: PlacesCacheRow | null | undefined;
}) {
  const lines = weekdayLines(lead.opening_hours);
  const addr = `${lead.address ?? 'Няма посочен адрес'}${lead.area ? ` (${lead.area})` : ''}`;
  return (
    <div className="lead-detail__factsgrid">
      <div className="lead-fact">
        <span className="lead-fact__label">Адрес и локация</span>
        <div className="lead-fact__value">{addr}</div>
      </div>
      <div className="lead-fact">
        <span className="lead-fact__label">Google Репутация</span>
        <div className="lead-fact__value">
          {lead.rating !== null ? (
            <span className="lead-fact__rating">
              <IconStar size={14} className="lead-fact__star" />
              <strong>{lead.rating}</strong>
              <span className="lead-fact__reviews">({lead.review_count ?? 0} отзива)</span>
            </span>
          ) : (
            'Няма отзиви'
          )}
        </div>
      </div>
      {lines.length > 0 && (
        <details className="lead-fact lead-fact--hours">
          <summary className="lead-fact__summary">
            <div>
              <span className="lead-fact__label">Работно време</span>
              <div className="lead-fact__value">{lines[0]}</div>
            </div>
            <span className="badge badge--neutral">График ({lines.length} дни) ▾</span>
          </summary>
          <div className="lead-fact__hours">
            {lines.map((line) => (
              <span key={line} className="lead-fact__hourpill">
                {line}
              </span>
            ))}
          </div>
        </details>
      )}
      {cached && (
        <div className="lead-detail__cached">
          <span>
            Google данни прочетени на:{' '}
            <strong>{new Date(cached.fetched_at).toLocaleDateString('bg-BG')}</strong>
          </span>
          {cached.formatted_address && <span> · {cached.formatted_address}</span>}
        </div>
      )}
    </div>
  );
}

export function LeadHeader({
  lead,
  cached,
}: {
  lead: LeadDetailRow;
  cached: PlacesCacheRow | null | undefined;
}) {
  const { data: stages = [] } = useStages();
  const setStage = useSetStage(lead.id);
  const toggleDnc = useToggleDnc(lead.id);
  const reanalyse = useDispatchRun();
  const tierClass = lead.tier === 'A' ? 'badge--tier-a' : 'badge--tier-b';

  return (
    <header className="lead-detail__header">
      <div className="lead-detail__titlearea">
        <div className="lead-detail__identity">
          <h2 className="lead-detail__title">
            {lead.business_name}
            {lead.name_source === null && <span className="badge badge--warn">непотвърден</span>}
          </h2>
          <div className="lead-detail__badges">
            {lead.tier && <span className={`badge ${tierClass}`}>Клас {lead.tier}</span>}
            {lead.lead_score !== null && (
              <span className="badge badge--score">{lead.lead_score} точки</span>
            )}
            {lead.priority && <span className="badge badge--neutral">{lead.priority}</span>}
            {lead.do_not_contact && <span className="badge badge--danger">Не безпокой</span>}
          </div>
        </div>
        <div className="lead-detail__workflow">
          <label className="lead-detail__stage-wrap">
            <span className="lead-detail__stage-label">Етап:</span>
            <select
              className="filter-select"
              aria-label="Етап на продажба"
              value={lead.sales_stage_id ?? ''}
              onChange={(e) =>
                setStage.mutate(e.target.value === '' ? null : Number(e.target.value))
              }
            >
              <option value="">Без етап</option>
              {stages.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={() => toggleDnc.mutate(!lead.do_not_contact)}
          >
            {lead.do_not_contact ? 'Върни контакт' : 'Не безпокой'}
          </button>
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            disabled={reanalyse.isPending}
            onClick={() => reanalyse.mutate({ stages: REANALYSE_STAGES, leadId: lead.id })}
          >
            {reanalyse.isPending ? 'Стартиране…' : 'Анализирай отново'}
          </button>
        </div>
      </div>
      {reanalyse.error && (
        <p className="lead-detail__err">Грешка при стартиране: {reanalyse.error.message}</p>
      )}
      <ContactStrip lead={lead} />
      <FactsGrid lead={lead} cached={cached} />
    </header>
  );
}
